import { Fragment } from "@wordpress/element";
import { Alert, Empty, Space, Table, Tabs } from "antd";
import { withGetScreen } from "react-getscreen";

import Guy_Table_NFL from "../components/guy-table-nfl";
import AchievementBadges from "../components/beer-achievement";
import { MlbTeamMobileCard } from "../components/guy-table";
import { useBlockConfig } from "../state";

const { TabPane } = Tabs;

const NFL_BLOCK_DEFAULTS = {
    show_standings: true,
    show_draft: true,
    expand_teams: true,
    show_schedule: false,
};

const teamLabel = (side) =>
    [side && side.city, side && side.name]
        .filter((x) => x != null && String(x).trim() !== "")
        .join(" ") || (side && side.key) || "";

const NflGameCard = ({ game }) => {
    const away = game.away || {};
    const home = game.home || {};
    const showScore = game.is_live || game.is_final;
    const awayScore =
        away.score == null || away.score === "" ? "–" : String(away.score);
    const homeScore =
        home.score == null || home.score === "" ? "–" : String(home.score);
    const status = game.is_live
        ? "Live"
        : game.is_final
        ? "Final"
        : game.kickoff_label || "Scheduled";

    return (
        <article className="wl-nfl-game">
            <div className="wl-nfl-game__team">
                <img
                    className="team_logo"
                    src={away.logo}
                    alt=""
                    width={36}
                    height={36}
                />
                <div className="wl-nfl-game__who">
                    <div className="wl-nfl-game__name">{teamLabel(away)}</div>
                    {away.owner ? (
                        <div className="wl-nfl-game__owner">{away.owner}</div>
                    ) : null}
                </div>
            </div>
            <div className="wl-nfl-game__mid">
                {showScore ? (
                    <div className="wl-nfl-game__score">
                        {awayScore} – {homeScore}
                    </div>
                ) : (
                    <div>@</div>
                )}
            </div>
            <div className="wl-nfl-game__team wl-nfl-game__team--home">
                <div className="wl-nfl-game__who">
                    <div className="wl-nfl-game__name">{teamLabel(home)}</div>
                    {home.owner ? (
                        <div className="wl-nfl-game__owner">{home.owner}</div>
                    ) : null}
                </div>
                <img
                    className="team_logo"
                    src={home.logo}
                    alt=""
                    width={36}
                    height={36}
                />
            </div>
            <div className="wl-nfl-game__meta">
                {status}
                {game.channel ? ` · ${game.channel}` : ""}
            </div>
        </article>
    );
};

const NflNextRow = ({ row }) => {
    const next = row.next_game;
    let detail = "No upcoming game listed";
    if (row.is_bye && !next) {
        detail = "Bye this week";
    } else if (next) {
        const vs = next.home ? "vs" : "@";
        detail = `${vs} ${next.opponent_name || next.opponent_key}`;
        if (next.kickoff_label) {
            detail += ` · ${next.kickoff_label}`;
        }
        if (next.channel) {
            detail += ` · ${next.channel}`;
        }
        if (row.is_bye) {
            detail = `Bye this week · next ${detail}`;
        }
    }

    return (
        <article className="wl-nfl-next">
            <img
                className="team_logo"
                src={row.logo}
                alt=""
                width={36}
                height={36}
            />
            <div className="wl-nfl-next__text">
                <div className="wl-nfl-game__owner">{row.owner}</div>
                <div className="wl-nfl-game__name">
                    {[row.city, row.name].filter(Boolean).join(" ")}
                </div>
                <div className="wl-nfl-next__detail">
                    {detail}
                </div>
            </div>
        </article>
    );
};

const NFL_Week_Tab = () => {
    const week =
        wl.nfl_main_info && wl.nfl_main_info.week
            ? wl.nfl_main_info.week
            : {};
    const games = Array.isArray(week.games) ? week.games : [];
    const nextGames = Array.isArray(week.next_games) ? week.next_games : [];
    const heading = week.week
        ? `Week ${week.week}`
        : "This week";

    return (
        <div>
            {week.note ? (
                <p className="wl-nfl-week-note">{week.note}</p>
            ) : null}
            <h3 className="wl-nfl-h">{heading}</h3>
            {games.length ? (
                <div className="wl-nfl-game-list">
                    {games.map((game) => (
                        <NflGameCard
                            key={game.game_key || `${game.away && game.away.key}-${game.home && game.home.key}`}
                            game={game}
                        />
                    ))}
                </div>
            ) : (
                <Empty description="No league games found for this week." />
            )}
            <h3 className="wl-nfl-h">Next games</h3>
            {nextGames.length ? (
                <div className="wl-nfl-next-list">
                    {nextGames.map((row) => (
                        <NflNextRow
                            key={`${row.owner}-${row.key}`}
                            row={row}
                        />
                    ))}
                </div>
            ) : (
                <Empty description="No upcoming games listed yet." />
            )}
        </div>
    );
};

const NFL_Wins_Main = (props) => {
    const cfg = { ...NFL_BLOCK_DEFAULTS, ...useBlockConfig() };

    const columns = [
        {
            title: "Standings",
            dataIndex: "standings",
            key: "standings",
            align: "left",
            render: (text, record) => (
                <Space size="small">
                    <span>{record.name}</span>
                    <AchievementBadges
                        beerEnabled={record.beerAchievementEnabled}
                        beerAnimation={record.beerAchievementAnimation}
                        achievements={record.achievements}
                        name={record.name}
                    />
                </Space>
            ),
        },
        {
            title: props.isMobile() ? "W" : "Total Wins",
            dataIndex: "total_wins",
            key: "total_wins",
            align: "center",
        },
        {
            title: props.isMobile() ? "L" : "Total Losses",
            dataIndex: "total_losses",
            key: "total_losses",
            align: "center",
        },
        {
            title: props.isMobile() ? "T" : "Total Ties",
            dataIndex: "total_ties",
            key: "total_ties",
            align: "center",
        },
        {
            title: props.isMobile() ? "%" : "Total Winning Percentage",
            dataIndex: "total_percentage",
            key: "total_percentage",
            align: "center",
        },
    ];

    const guys = Array.isArray(wl.nfl_main_info && wl.nfl_main_info.guys)
        ? wl.nfl_main_info.guys
        : [];

    const data = guys.map((guy) => {
        return {
            key: guy.nfl_guy_2020,
            name: guy.nfl_guy_2020,
            beerAchievementEnabled: Boolean(guy.beer_achievement_enabled),
            beerAchievementAnimation: guy.beer_achievement_animation,
            achievements: guy.league_achievements,
            total_wins: guy.total_wins,
            total_losses: guy.total_losses,
            total_ties: guy.total_ties,
            total_percentage: guy.pct,
            team_data: guy.team_standings_data,
        };
    });

    const draftSummary = Array.isArray(
        wl.nfl_main_info && wl.nfl_main_info.draft_summary
    )
        ? wl.nfl_main_info.draft_summary
        : [];

    const draftData = draftSummary.map((row, idx) => {
        const teamKey = row.key;
        return {
            ...row,
            nfl_team_key: teamKey,
            key: `draft-${idx}-${row.pick}-${teamKey}-${row.owner}`,
        };
    });

    const tabsSpec = [];
    if (cfg.show_standings) {
        tabsSpec.push({
            key: "standings",
            tab: "Standings",
            content: (
                <Table
                    className="wl-nfl-standings-table"
                    size="middle"
                    columns={columns}
                    dataSource={data}
                    expandable={
                        cfg.expand_teams
                            ? {
                                  expandedRowRender: (record) => (
                                      <Guy_Table_NFL
                                          team_data={record.team_data}
                                      />
                                  ),
                              }
                            : undefined
                    }
                    pagination={false}
                    scroll={props.isMobile() ? { x: "max-content" } : undefined}
                />
            ),
        });
    }
    if (cfg.show_draft) {
        tabsSpec.push({
            key: "draft",
            tab: `Draft order${
                draftData.length ? ` (${draftData.length})` : ""
            }`,
            content: draftData.length ? (
                <div
                    className="wl-mlb-draft-card-stack"
                    aria-label="Draft order"
                >
                    {draftData.map((row) => {
                        const cardRecord = {
                            key: row.nfl_team_key || row.key,
                            city: row.city,
                            name: row.name,
                            logo: row.logo,
                            wins: row.wins,
                            losses: row.losses,
                            ties: row.ties,
                            percentage: row.percentage,
                        };
                        const pick =
                            row.pick == null ? "—" : String(row.pick);
                        return (
                            <MlbTeamMobileCard
                                key={row.key}
                                record={cardRecord}
                                metaPrefix={`#${pick} · ${row.owner}`}
                            />
                        );
                    })}
                </div>
            ) : (
                <Empty description="No draft data." />
            ),
        });
    }
    if (cfg.show_schedule) {
        const weekNum =
            wl.nfl_main_info &&
            wl.nfl_main_info.week &&
            wl.nfl_main_info.week.week
                ? wl.nfl_main_info.week.week
                : 0;
        tabsSpec.push({
            key: "week",
            tab: weekNum ? `This week (${weekNum})` : "This week",
            content: <NFL_Week_Tab />,
        });
    }

    if (!tabsSpec.length) {
        return (
            <Alert
                type="info"
                showIcon
                message="Nothing to display"
                description="Enable at least one section in the block settings (sidebar) to show standings or draft order."
            />
        );
    }

    return (
        <Fragment>
            <Tabs
                defaultActiveKey={tabsSpec[0].key}
                className="wl-mlb-tabs"
                animated={{ inkBar: true, tabPane: false }}
            >
                {tabsSpec.map(({ key, tab, content }) => (
                    <TabPane tab={tab} key={key}>
                        {content}
                    </TabPane>
                ))}
            </Tabs>
        </Fragment>
    );
};

export default withGetScreen(NFL_Wins_Main);
